import React, { useMemo, useState } from 'react';
import { Button, DatePicker, Input, List, Select, Tag } from 'antd';
import type { Dispatch, SetStateAction } from 'react';
import locale from 'antd/es/date-picker/locale/zh_CN';
import 'moment/locale/zh-cn';
import type { DockingFilter, DockingStatistics, ParkDockingSummary } from '../../types/docking';
import { DEFAULT_DOCKING_FILTER } from '../../types/docking';

import moment from 'moment';

const LEVEL_TAG: Record<string, { color: string; text: string }> = {
  national: { color: '#f5222d', text: '国家级' },
  provincial: { color: '#1890ff', text: '省级' },
  pending: { color: '#faad14', text: '待开发' },
};

export interface DockingSidebarProps {
  statistics: DockingStatistics;
  parkSummaries: ParkDockingSummary[];
  filter: DockingFilter;
  setFilter: Dispatch<SetStateAction<DockingFilter>>;
  selectedParkId: string | null;
  onSelectPark: (parkId: string) => void;
}

export function DockingSidebar(props: DockingSidebarProps): React.ReactElement {
  const {
    statistics,
    parkSummaries,
    filter,
    setFilter,
    selectedParkId,
    onSelectPark,
  } = props;

  const [keywordInput, setKeywordInput] = useState('');
  const [dateFrom, setDateFrom] = useState<moment.Moment | null>(null);
  const [dateTo, setDateTo] = useState<moment.Moment | null>(null);

  const applyDateRange = (from: moment.Moment | null, to: moment.Moment | null) => {
    if (!from && !to) {
      setFilter((prev) => ({ ...prev, dateRange: null }));
    } else {
      const f = from ? from.format('YYYY-MM-DD') : '0000-01-01';
      const t = to ? to.format('YYYY-MM-DD') : '9999-12-31';
      setFilter((prev) => ({ ...prev, dateRange: [f, t] }));
    }
  };

  /** 与园区查询侧栏一致：关键字仅本地筛选列表，不写入 filter */
  const keywordFilteredSummaries = useMemo(() => {
    const keyword = keywordInput.trim().toLowerCase();
    if (!keyword) return parkSummaries;
    return parkSummaries.filter((item) => {
      const text = [item.parkName, item.province, item.city]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return text.includes(keyword);
    });
  }, [keywordInput, parkSummaries]);

  return (
    <aside className="dash-panel dash-panel--left">
      {/* Statistics Cards */}
      <div className="dash-section">
        <div className="dash-field-label" style={{ marginBottom: 8 }}>已对接园区数量</div>
        <div className="dash-stat-grid">
          <div className="dash-stat-card">
            <div className="dash-stat-card__label">国家级</div>
            <div className="dash-stat-card__value">{statistics.dockedNational}</div>
          </div>
          <div className="dash-stat-card">
            <div className="dash-stat-card__label">省级</div>
            <div className="dash-stat-card__value dash-stat-card__value--accent">{statistics.dockedProvincial}</div>
          </div>
          <div className="dash-stat-card">
            <div className="dash-stat-card__label">待开发</div>
            <div className="dash-stat-card__value dash-stat-card__value--pending">{statistics.dockedPending}</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="dash-section">
        <div className="dash-field-row">
          <div className="dash-field-label">园区搜索</div>
          <Button
            type="link"
            size="small"
            className="dash-inline-link-btn"
            onClick={() => {
              setFilter(DEFAULT_DOCKING_FILTER);
              setKeywordInput('');
              setDateFrom(null);
              setDateTo(null);
            }}
          >
            重置筛选
          </Button>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Input
            allowClear
            value={keywordInput}
            placeholder="请输入园区关键字"
            onChange={(e) => setKeywordInput(e.target.value)}
          />
          <Button
            type="default"
            disabled={!keywordInput.trim()}
            onClick={() => setKeywordInput('')}
          >
            清空
          </Button>
        </div>

        <div className="dash-field-label dash-field-label--spaced">园区等级</div>
        <Select
          className="dash-select"
          dropdownClassName="dash-select-dropdown"
          style={{ width: '100%' }}
          value={filter.parkLevel}
          onChange={(v) => setFilter((prev) => ({ ...prev, parkLevel: v }))}
          options={[
            { label: '全部', value: 'all' },
            { label: '国家级', value: 'national' },
            { label: '省级', value: 'provincial' },
            { label: '待开发', value: 'pending' },
          ]}
        />

        <div className="dash-field-label dash-field-label--spaced">对接时间范围</div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <DatePicker
            locale={locale}
            placeholder="开始日期"
            allowClear
            style={{ flex: 1 }}
            value={dateFrom}
            onChange={(d) => {
              setDateFrom(d);
              applyDateRange(d, dateTo);
            }}
          />
          <span style={{ color: 'rgba(255,255,255,0.45)' }}>~</span>
          <DatePicker
            locale={locale}
            placeholder="结束日期"
            allowClear
            style={{ flex: 1 }}
            value={dateTo}
            onChange={(d) => {
              setDateTo(d);
              applyDateRange(dateFrom, d);
            }}
          />
        </div>

        <div className="dash-field-label dash-field-label--spaced">项目成熟度</div>
        <Select
          allowClear
          placeholder="请选择"
          className="dash-select"
          dropdownClassName="dash-select-dropdown"
          style={{ width: '100%' }}
          value={filter.maturity}
          onChange={(v) => setFilter((prev) => ({ ...prev, maturity: v ?? null }))}
          options={[
            { label: '1 星', value: 1 },
            { label: '2 星', value: 2 },
            { label: '3 星', value: 3 },
            { label: '4 星', value: 4 },
            { label: '5 星', value: 5 },
          ]}
        />
      </div>

      {/* Park list */}
      <div className="dash-section dash-section--grow">
        <div className="dash-field-label">
          对接园区列表（{keywordFilteredSummaries.length} 个园区）
        </div>
        <div className="dash-list-wrap">
          <List
            size="small"
            dataSource={keywordFilteredSummaries}
            locale={{ emptyText: '暂无对接记录' }}
            renderItem={(item) => {
              const isSelected = selectedParkId === item.parkId;
              const tag = LEVEL_TAG[item.parkLevel];
              return (
                <List.Item
                  className={
                    isSelected ? 'dash-list-item dash-list-item--active' : 'dash-list-item'
                  }
                  onClick={() => onSelectPark(item.parkId)}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {tag && <Tag color={tag.color} style={{ marginRight: 0, fontSize: 11, lineHeight: '18px', padding: '0 4px' }}>{tag.text}</Tag>}
                      <span className="dash-list-item__name" style={{ flex: 1 }}>{item.parkName}</span>
                      <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12 }}>
                        {item.recordCount} 次
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>
                      最近：{item.latestDockedAt} · {item.latestCounterpartyOrg}
                    </div>
                  </div>
                </List.Item>
              );
            }}
          />
        </div>
      </div>
    </aside>
  );
}
